import { type FC } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";

import { Col, Row } from "~/common/components/Layout";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "~/common/components/Link";
import { PRIVACY_PAGE } from "~/utils/url/staticUrls";
import { Stack, TextField } from "~/common/mui";
import { Alert } from "~/common/mui/Alert/Alert";
import { validateEmail } from "~/utils/emailValidator";

interface Props {
  register: UseFormReturn["register"];
  control: UseFormReturn["control"];
  errors: UseFormReturn["formState"]["errors"];
}

const ShareConfigurationsViaEmailPopupForm: FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  return (
    <Stack gap={2} className="pt-1">
      <Row>
        <Col size={12}>
          <Controller
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label={t("salesPartner.message.shareViaEmail.label.email")}
                error={!!error}
                inputRef={ref}
                {...inputProps}
              />
            )}
            name={"email"}
            control={props.control}
            rules={{
              validate: validateEmail(t("salesPartner.message.shareViaEmail.email.message"), "error.invalidEmail"),
            }}
            defaultValue={null}
          />
          {props.errors.email && (
            <Alert severity={"error"}>{t(String(props.errors.email.message ?? "error.invalidEmail"))}</Alert>
          )}
        </Col>
      </Row>
      <Row>
        <Col size={6}>
          <Controller
            render={({ field }) => (
              <TextField fullWidth label={t("salesPartner.message.shareViaEmail.label.first.name")} {...field} />
            )}
            name={"firstName"}
            control={props.control}
            rules={{ required: false }}
            defaultValue={null}
          />
        </Col>
        <Col size={6}>
          <Controller
            render={({ field }) => (
              <TextField fullWidth label={t("salesPartner.message.shareViaEmail.label.last.name")} {...field} />
            )}
            name={"lastName"}
            control={props.control}
            rules={{ required: false }}
            defaultValue={null}
          />
        </Col>
      </Row>
      <Row>
        <Col size={12}>
          <Controller
            render={({ field: { ref, ...props } }) => (
              <TextField
                variant={"outlined"}
                multiline
                fullWidth
                inputProps={{ sx: { height: "108px !important" }, maxLength: 512 }}
                label={t("salesPartner.message.shareViaEmail.label.note")}
                inputRef={ref}
                {...props}
              />
            )}
            name={"note"}
            control={props.control}
            rules={{ required: false, maxLength: 512 }}
            defaultValue={""}
          />
        </Col>
      </Row>
      <Row className={"mt-3"}>
        <Col size={12}>
          <Controller
            render={({ field: { value, onChange } }) => (
              <CheckboxWithLabel checked={value} onCheck={onChange}>
                <Trans i18nKey="salesPartner.message.shareViaEmail.checkbox">
                  I hereby agree to forwarding contact information from my user profile and my comments to the selected
                  sales partner for the purpose of contacting and requesting an offer.
                  <Link href={PRIVACY_PAGE} target="_blank">
                    Privacy Policy
                  </Link>
                </Trans>
              </CheckboxWithLabel>
            )}
            name={"consent"}
            control={props.control}
            rules={{ required: true }}
            defaultValue={false}
          />
          {props.errors.consent && (
            <Alert severity={"error"}>{t("salesPartner.message.shareViaEmail.consent.message")}</Alert>
          )}
        </Col>
      </Row>
    </Stack>
  );
};

export default ShareConfigurationsViaEmailPopupForm;
